import React from "react";
import { TopBar } from "@/components/TopBar";
import { ROLES, ROLE_LABELS } from "@/lib/roles";
import {
  apiListTarif,
  apiCreateTarif,
  apiUpdateTarif,
  formatApiErrorDetail,
} from "@/lib/api";
import { Loader2, Plus, Save, X, RefreshCw, Power } from "lucide-react";
import * as W from "@/lib/waktu";

const KATEGORI = ["BANNER", "DTF", "JERSEY", "CUSTOM"];
const today = () => W.hariIni();
const EMPTY = {
  role: "desain",
  kategori_produk: "BANNER",
  jenis_pekerjaan: "",
  produk: "",
  nominal: "",
  satuan: "pcs",
  periode_mulai: today(),
  periode_selesai: "",
  is_active: true,
};

export default function TarifManagement() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [notice, setNotice] = React.useState(null);
  const [form, setForm] = React.useState(EMPTY);
  const [editingId, setEditingId] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await apiListTarif());
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const flash = (m) => {
    setNotice(m);
    setTimeout(() => setNotice(null), 2500);
  };
  const resetForm = () => {
    setForm(EMPTY);
    setEditingId(null);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    const payload = {
      ...form,
      nominal: parseFloat(form.nominal),
      produk: form.produk || null,
      periode_selesai: form.periode_selesai || null,
    };
    try {
      if (editingId) {
        await apiUpdateTarif(editingId, payload);
        flash("Tarif diperbarui");
      } else {
        await apiCreateTarif(payload);
        flash("Tarif ditambahkan");
      }
      resetForm();
      await load();
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (t) => {
    setEditingId(t.id);
    setForm({
      role: t.role,
      kategori_produk: t.kategori_produk,
      jenis_pekerjaan: t.jenis_pekerjaan,
      produk: t.produk || "",
      nominal: String(t.nominal),
      satuan: t.satuan,
      periode_mulai: (t.periode_mulai || "").slice(0, 10),
      periode_selesai: (t.periode_selesai || "").slice(0, 10),
      is_active: t.is_active,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleActive = async (t) => {
    setError(null);
    try {
      await apiUpdateTarif(t.id, { is_active: !t.is_active });
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const inputCls =
    "mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500/60";
  const labelCls =
    "text-[13px] font-semibold text-slate-200";

  return (
    <div data-testid="tarif-management-page" className="min-h-screen bg-slate-950 text-slate-200">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div>
          <h1
            className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-100"
          >
            Manajemen Tarif
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Konfigurasi tarif per role, kategori produk, jenis pekerjaan &amp; periode.
          </p>
        </div>

        {notice && (
          <div data-testid="tarif-notice" className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-sm text-emerald-300">
            {notice}
          </div>
        )}
        {error && (
          <div data-testid="tarif-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">
            {error}
          </div>
        )}

        <form
          data-testid="tarif-form"
          onSubmit={onSubmit}
          className="rounded-xl border border-slate-800 bg-slate-900/60 p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          <div>
            <label className={labelCls}>Role</label>
            <select data-testid="tarif-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls}>
              {ROLES.map((r) => (
                <option key={r} value={r}>{ROLE_LABELS[r]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Kategori Produk</label>
            <select data-testid="tarif-kategori" value={form.kategori_produk} onChange={(e) => setForm({ ...form, kategori_produk: e.target.value })} className={inputCls}>
              {KATEGORI.map((k) => (<option key={k} value={k}>{k}</option>))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Jenis Pekerjaan</label>
            <input data-testid="tarif-jenis" value={form.jenis_pekerjaan} onChange={(e) => setForm({ ...form, jenis_pekerjaan: e.target.value })} required className={inputCls} placeholder="cetak / press / jahit" />
          </div>
          <div>
            <label className={labelCls}>Produk (opsional)</label>
            <input data-testid="tarif-produk" value={form.produk} onChange={(e) => setForm({ ...form, produk: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Nominal</label>
            <input data-testid="tarif-nominal" type="number" min="0" step="any" value={form.nominal} onChange={(e) => setForm({ ...form, nominal: e.target.value })} required className={inputCls} placeholder="10000" />
          </div>
          <div>
            <label className={labelCls}>Satuan</label>
            <input data-testid="tarif-satuan" value={form.satuan} onChange={(e) => setForm({ ...form, satuan: e.target.value })} className={inputCls} placeholder="pcs / meter / m2" />
          </div>
          <div>
            <label className={labelCls}>Periode Mulai</label>
            <input data-testid="tarif-mulai" type="date" value={form.periode_mulai} onChange={(e) => setForm({ ...form, periode_mulai: e.target.value })} required className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Periode Selesai (opsional)</label>
            <input data-testid="tarif-selesai" type="date" value={form.periode_selesai} onChange={(e) => setForm({ ...form, periode_selesai: e.target.value })} className={inputCls} />
          </div>
          <div className="flex items-center gap-2 sm:col-span-2 lg:col-span-4">
            <label className="inline-flex items-center gap-2 text-sm text-slate-300">
              <input data-testid="tarif-active" type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              Aktif
            </label>
            <div className="ml-auto flex items-center gap-2">
              {editingId && (
                <button type="button" data-testid="tarif-cancel" onClick={resetForm} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:text-slate-100">
                  <X size={15} /> Batal
                </button>
              )}
              <button data-testid="tarif-submit" type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-5 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60">
                {saving ? <Loader2 className="animate-spin" size={16} /> : editingId ? <Save size={16} /> : <Plus size={16} />}
                {editingId ? "Simpan" : "Tambah"}
              </button>
            </div>
          </div>
        </form>

        <div className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
            <h2 className="text-[15px] font-bold text-soq-biru-tua">Daftar Tarif</h2>
            <button data-testid="tarif-refresh" onClick={load} className="inline-flex items-center gap-2 text-xs font-mono text-slate-400 hover:text-slate-200">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
          {loading ? (
            <div className="p-8 flex items-center justify-center text-slate-400"><Loader2 className="animate-spin mr-2" size={18} /> Memuat…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="tarif-table">
                <thead>
                  <tr className="text-left text-xs font-semibold text-slate-500 bg-slate-900 border-b border-slate-800">
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">Kategori</th>
                    <th className="px-4 py-3">Jenis</th>
                    <th className="px-4 py-3">Produk</th>
                    <th className="px-4 py-3 text-right">Nominal</th>
                    <th className="px-4 py-3">Periode</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => (
                    <tr key={t.id} data-testid={`tarif-row-${t.id}`} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                      <td className="px-4 py-3 text-slate-300">{ROLE_LABELS[t.role] || t.role}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-300">{t.kategori_produk}</td>
                      <td className="px-4 py-3 text-slate-300">{t.jenis_pekerjaan}</td>
                      <td className="px-4 py-3 text-slate-400">{t.produk || "—"}</td>
                      <td className="px-4 py-3 text-right font-mono text-slate-200">{Number(t.nominal).toLocaleString("id-ID")}<span className="text-slate-500"> /{t.satuan}</span></td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-400">{(t.periode_mulai || "").slice(0, 10)} → {(t.periode_selesai || "∞").slice(0, 10)}</td>
                      <td className="px-4 py-3">
                        <span data-testid={`tarif-status-${t.id}`} className={`text-xs font-mono px-2 py-1 rounded-md border ${t.is_active ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-slate-600 bg-slate-700/30 text-slate-400"}`}>
                          {t.is_active ? "Aktif" : "Nonaktif"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button data-testid={`tarif-edit-${t.id}`} onClick={() => startEdit(t)} className="rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-amber-500/40 hover:text-amber-300">Edit</button>
                          <button data-testid={`tarif-toggle-${t.id}`} onClick={() => toggleActive(t)} title="Aktif/Nonaktif" className="inline-flex items-center rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-red-500/40 hover:text-red-300"><Power size={13} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">Belum ada tarif.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
